import { Microphone, X, ShieldCheck } from "@phosphor-icons/react";

/**
 * AI Data Consent Modal — required by Apple Guideline 5.1.1(i) / 5.1.2(i).
 * Shown once, before the user's FIRST recording, with explicit disclosure of:
 *   - What data is sent (audio file → transcript text)
 *   - Who receives it (OpenAI — Whisper + GPT-5.2)
 *   - How long it is retained by the third party (per OpenAI's API policy: not used for training, deleted within 30 days)
 *   - User must tap "I agree" before recording proceeds.
 *
 * Consent is persisted server-side on the user (`ai_consent_at` ISO timestamp)
 * AND mirrored to localStorage as a fallback so review sandboxes see it instantly.
 */
export default function AIConsentModal({ open, onAgree, onCancel }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6"
      style={{ background: "rgba(42, 51, 38, 0.55)" }}
      data-testid="ai-consent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-consent-title"
    >
      <div className="card w-full max-w-lg p-7 sm:p-9 relative animate-fade-up">
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-3 right-3 text-ink-500 hover:text-ink-900 p-2 rounded-full hover:bg-bone-200"
          aria-label="Close"
          data-testid="ai-consent-close-btn"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-full bg-brand/10 text-brand flex items-center justify-center">
            <ShieldCheck size={22} weight="fill" />
          </div>
          <div>
            <p className="eyebrow">Before your first entry</p>
            <h2 id="ai-consent-title" className="font-serif text-2xl mt-1">
              How Hush handles your voice
            </h2>
          </div>
        </div>

        <p className="text-ink-700 leading-relaxed mb-4">
          Hush uses AI to transcribe and reflect on what you say. We want you to know
          exactly what happens to your words before you start.
        </p>

        <ul className="space-y-3 text-[15px] text-ink-700 leading-relaxed mb-6">
          <li className="flex items-start gap-2">
            <Microphone size={18} weight="bold" className="text-brand shrink-0 mt-[3px]" />
            <span>
              Your <b>audio recording</b> is sent to <b>OpenAI Whisper</b> to be turned into text.
              The audio file is <b>not stored</b> on our servers.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Microphone size={18} weight="bold" className="text-brand shrink-0 mt-[3px] opacity-0" />
            <span>
              The <b>transcript text</b> is sent to <b>OpenAI GPT-5.2</b> to detect a mood, generate
              short emotion tags, and write a brief reflection back to you.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Microphone size={18} weight="bold" className="text-brand shrink-0 mt-[3px] opacity-0" />
            <span>
              OpenAI processes data via their API with <b>no training</b> on your content and deletes
              it within 30 days. Read their{" "}
              <a
                href="https://openai.com/policies/api-data-usage-policies"
                target="_blank"
                rel="noreferrer"
                className="text-brand underline"
              >
                API data usage policy
              </a>
              .
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Microphone size={18} weight="bold" className="text-brand shrink-0 mt-[3px] opacity-0" />
            <span>
              Only your <b>transcript</b>, the detected mood, and the AI reflection are saved to your
              Hush journal. You can delete any entry — or your whole account — at any time.
            </span>
          </li>
        </ul>

        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn-ghost flex-1"
            data-testid="ai-consent-decline-btn"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={onAgree}
            className="btn-primary flex-1"
            data-testid="ai-consent-agree-btn"
          >
            I agree — start recording
          </button>
        </div>

        <p className="text-[11px] text-ink-500 text-center mt-5 leading-relaxed">
          See our full <a href="/privacy" className="underline hover:text-ink-900">Privacy Policy</a> for details.
        </p>
      </div>
    </div>
  );
}
